import React, { useState } from 'react';
import {
  Card,
  Typography,
  Input,
  Switch,
  Button,
  Divider,
  Collapse,
  Alert,
  Segmented,
  App,
  Tag,
} from 'antd';
import { normalizeBase } from '../utils';
import { useGlobalStore } from '@digiwedge/global-state';
import { useIdp } from '../../auth/IdpProvider.js';

function useIdpSafe(): ReturnType<typeof useIdp> | null {
  try {
    return useIdp();
  } catch {
    return null;
  }
}

export default function IdpDiagnosticsPanel({
  idpBase,
  audience,
  onApplied,
}: {
  idpBase: string;
  audience: string;
  onApplied?: () => void;
}): React.ReactElement {
  const { message } = App.useApp();
  const { Text } = Typography;
  const [jsonView, setJsonView] = useState<'raw' | 'tree'>('raw');
  const [jsonFilter, setJsonFilter] = useState('');
  const [diagParsed, setDiagParsed] = useState<unknown | null>(null);
  const idp = useIdpSafe();

  // Track CSRF cookie presence for visibility
  const readXsrf = () => {
    try {
      const cookie = typeof document !== 'undefined' ? document.cookie : '';
      const hit = cookie
        .split(';')
        .map((s) => s.trim())
        .find((p) => p.startsWith('XSRF-TOKEN='));
      if (!hit) return undefined;
      return decodeURIComponent(hit.split('=')[1] || '');
    } catch {
      return undefined;
    }
  };
  const [xsrf, setXsrf] = useState<string | undefined>(() => readXsrf());

  return (
    <Card title="IDP Direct Login (Diagnostics)" style={{ marginTop: 16 }}>
      <Text type="secondary">
        Posts directly to the IDP login endpoint and shows the raw JSON response. Use this to
        validate credentials, audience, and token issuance without involving the app’s auth flow.
        Tokens are auto-applied on success.
      </Text>
      <Divider />
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Input style={{ maxWidth: 320 }} placeholder="Email" type="email" id="diag-email" />
        <Input.Password style={{ maxWidth: 240 }} placeholder="Password" id="diag-password" />
        <Switch id="diag-remember" />
        <Text type="secondary">Remember</Text>
        <Tag color={xsrf ? 'green' : 'default'}>CSRF cookie: {xsrf ? 'present' : 'missing'}</Tag>
        <Button
          size="small"
          onClick={async () => {
            try {
              const base = normalizeBase(idpBase);
              if (idp) {
                await idp.axios.get('/api/auth/csrf');
              } else {
                await fetch(`${base}/api/auth/csrf`, { credentials: 'include' });
              }
              setTimeout(() => setXsrf(readXsrf()), 0);
              message?.success('Fetched CSRF token');
            } catch {
              message?.warning('Failed to fetch CSRF token');
            }
          }}
        >
          Ensure CSRF cookie
        </Button>
        <Button
          type="primary"
          onClick={async () => {
            const base = normalizeBase(idpBase);
            const emailVal =
              (document.getElementById('diag-email') as HTMLInputElement | null)?.value?.trim() ||
              '';
            const passVal =
              (document.getElementById('diag-password') as HTMLInputElement | null)?.value || '';
            const rememberVal = Boolean(
              (document.getElementById('diag-remember') as HTMLInputElement | null)?.checked,
            );
            try {
              let status = 0;
              let ok = false;
              let body: unknown = null;
              const baseUrl = normalizeBase(idpBase);
              if (idp) {
                const res = await idp.axios.post(
                  '/api/auth/login',
                  { email: emailVal, password: passVal },
                  {
                    headers: {
                      'Content-Type': 'application/json',
                      Accept: 'application/json',
                      'X-Client-Audience': audience,
                    },
                  },
                );
                status = res.status;
                ok = res.status >= 200 && res.status < 300;
                body = res.data;
              } else {
                const xsrfVal = readXsrf();
                const res = await fetch(`${baseUrl}/api/auth/login`, {
                  method: 'POST',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Client-Audience': audience,
                    ...(xsrfVal ? { 'X-XSRF-Token': xsrfVal } : {}),
                  },
                  body: JSON.stringify({ email: emailVal, password: passVal }),
                });
                status = res.status;
                ok = res.ok;
                const text = await res.text();
                try {
                  body = text ? JSON.parse(text) : null;
                } catch {
                  body = { raw: text };
                }
              }
              const pretty = JSON.stringify(
                {
                  status,
                  ok,
                  audience,
                  endpoint: `${base}/api/auth/login`,
                  body,
                },
                null,
                2,
              );
              const out = document.getElementById('diag-output') as HTMLTextAreaElement | null;
              if (out) out.value = pretty;
              setDiagParsed({ status, ok, audience, endpoint: `${base}/api/auth/login`, body });
              if (ok && body && typeof body === 'object') {
                const candidate = body as Record<string, unknown>;
                const at =
                  typeof candidate['accessToken'] === 'string'
                    ? (candidate['accessToken'] as string)
                    : undefined;
                const rt =
                  typeof candidate['refreshToken'] === 'string'
                    ? (candidate['refreshToken'] as string)
                    : undefined;
                const j =
                  typeof candidate['jti'] === 'string' ? (candidate['jti'] as string) : undefined;
                if (at) {
                  useGlobalStore.getState().login(at, rt, j, rememberVal);
                  message?.success('Login successful • tokens applied');
                  onApplied?.();
                } else if ((candidate['mfaRequired'] as boolean) === true) {
                  message?.info('MFA required — complete MFA flow to proceed');
                }
              }
              setXsrf(readXsrf());
            } catch (e) {
              const out = document.getElementById('diag-output') as HTMLTextAreaElement | null;
              if (out) out.value = String(e);
            }
          }}
        >
          Run login
        </Button>
        <Button
          onClick={async () => {
            const out = document.getElementById('diag-output') as HTMLTextAreaElement | null;
            if (!out?.value) return;
            try {
              await navigator.clipboard.writeText(out.value);
              message?.success('Copied JSON to clipboard');
            } catch {
              try {
                const ta = document.getElementById('diag-output') as HTMLTextAreaElement | null;
                if (ta) {
                  ta.focus();
                  ta.select();
                  document.execCommand('copy');
                  message?.success('Copied JSON to clipboard');
                }
              } catch {
                message?.warning('Copy failed. Select and copy manually.');
              }
            }
          }}
        >
          Copy JSON
        </Button>
      </div>
      <div style={{ marginTop: 12 }}>
        <Collapse
          size="small"
          items={[
            {
              key: 'raw-json',
              label: 'Raw JSON (click to expand)',
              children: (
                <div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                    <Segmented
                      options={[
                        { label: 'Raw', value: 'raw' },
                        { label: 'Tree', value: 'tree' },
                      ]}
                      value={jsonView}
                      onChange={(v) => setJsonView(v as 'raw' | 'tree')}
                    />
                    {jsonView === 'tree' && (
                      <Input
                        placeholder="Search keys"
                        size="small"
                        style={{ maxWidth: 240 }}
                        value={jsonFilter}
                        onChange={(e) => setJsonFilter(e.target.value)}
                      />
                    )}
                  </div>
                  {jsonView === 'raw' ? (
                    <textarea id="diag-output" style={{ width: '100%', minHeight: 240 }} readOnly />
                  ) : diagParsed ? (
                    <div
                      style={{
                        maxHeight: 360,
                        overflow: 'auto',
                        border: '1px solid #f0f0f0',
                        borderRadius: 6,
                        padding: 8,
                      }}
                    >
                      {/* Keep raw JSON view in tree mode simple */}
                      <pre style={{ margin: 0 }}>{JSON.stringify(diagParsed, null, 2)}</pre>
                    </div>
                  ) : (
                    <Alert type="info" title="Run login to populate JSON view" />
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>
    </Card>
  );
}
